import { useEffect, useState } from "react";
import { api, LOGO_URL, currency, fmtDate } from "@/lib/api";

// Neat, printable invoice document. Read-only display.
// Always uses live Settings for company/bank details so any update reflects immediately.
export default function InvoiceDocument({ invoice, settings: settingsProp }) {
  const [liveSettings, setLiveSettings] = useState(null);

  useEffect(() => {
    if (!settingsProp) {
      api.get("/settings").then(({ data }) => setLiveSettings(data)).catch(() => {});
    }
  }, [settingsProp]);

  if (!invoice) return null;

  // Prefer live settings > passed-in prop > snapshot baked into invoice
  const settings = settingsProp || liveSettings || {};
  const co = settings.company_details || invoice.company_details || {};
  const bank = settings.bank_details || invoice.bank_details || {};

  return (
    <div id="invoice-print" className="bg-white p-4 sm:p-8 md:p-10" data-testid="invoice-document">
      <div className="flex justify-between items-start pb-6 border-b-2" style={{ borderColor: "#032352" }}>
        <div className="max-w-xs">
          <img
            src={LOGO_URL}
            crossOrigin="anonymous"
            alt="J&L Industrial Supplies"
            className="h-20 object-contain mb-3"
          />
          <div className="text-xs text-[#5B5C5F] leading-relaxed" data-testid="invoice-company-details">
            <p className="font-semibold text-[#032352] text-sm">
              {co.name || "J&L Industrial Supplies"}
            </p>
            {co.address && <p className="whitespace-pre-line">{co.address}</p>}
            {co.phone && <p>Tel: {co.phone}</p>}
            {co.email && <p>{co.email}</p>}
            {co.website && <p>{co.website}</p>}
            {co.vat_number && <p>VAT No: {co.vat_number}</p>}
            {co.company_number && <p>Company No: {co.company_number}</p>}
          </div>
        </div>
        <div className="text-right">
          <h2 className="font-heading text-3xl font-extrabold text-[#032352]">INVOICE</h2>
          <p className="font-mono-num text-lg text-[#5B5C5F] mt-1">{invoice.invoice_number}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 py-6">
        <div>
          <p className="text-xs uppercase tracking-wider text-[#5B5C5F] mb-2">Bill To</p>
          <p className="font-semibold text-[#032352]">{invoice.customer_name}</p>
          {invoice.customer_address && (
            <p className="text-sm text-[#5B5C5F] whitespace-pre-line">{invoice.customer_address}</p>
          )}
          {invoice.customer_email && <p className="text-sm text-[#5B5C5F]">{invoice.customer_email}</p>}
          {invoice.customer_phone && <p className="text-sm text-[#5B5C5F]">{invoice.customer_phone}</p>}
        </div>
        <div className="text-right space-y-1">
          <div className="flex justify-between gap-8">
            <span className="text-xs uppercase tracking-wider text-[#5B5C5F]">Invoice Date</span>
            <span className="text-sm font-medium text-[#032352]">{fmtDate(invoice.invoice_date)}</span>
          </div>
          <div className="flex justify-between gap-8">
            <span className="text-xs uppercase tracking-wider text-[#5B5C5F]">Due Date</span>
            <span className="text-sm font-medium text-[#032352]">{fmtDate(invoice.due_date)}</span>
          </div>
          {invoice.order_number && (
            <div className="flex justify-between gap-8">
              <span className="text-xs uppercase tracking-wider text-[#5B5C5F]">Order No</span>
              <span className="text-sm font-medium text-[#032352]" data-testid="invoice-order-number">
                {invoice.order_number}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[520px]">
        <thead>
          <tr style={{ backgroundColor: "#032352" }} className="text-white">
            <th className="text-left p-3 font-medium">Description</th>
            <th className="text-center p-3 font-medium w-16">Qty</th>
            <th className="text-right p-3 font-medium w-28">Unit Price</th>
            <th className="text-center p-3 font-medium w-16">VAT</th>
            <th className="text-right p-3 font-medium w-28">Amount</th>
          </tr>
        </thead>
        <tbody>
          {invoice.line_items.map((it, i) => (
            <tr key={i} className="border-b border-slate-200">
              <td className="p-3 text-[#032352]">
                {it.title}
                {it.variant ? <span className="text-[#5B5C5F]"> — {it.variant}</span> : null}
              </td>
              <td className="p-3 text-center font-mono-num text-[#5B5C5F]">{it.quantity}</td>
              <td className="p-3 text-right font-mono-num text-[#5B5C5F]">{currency(it.price)}</td>
              <td className="p-3 text-center text-xs text-[#5B5C5F]">
                {it.vat_applicable ? "20%" : "—"}
              </td>
              <td className="p-3 text-right font-mono-num text-[#032352]">
                {currency(it.price * it.quantity)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      <div className="flex justify-end mt-6">
        <div className="w-72 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-[#5B5C5F]">Subtotal</span>
            <span className="font-mono-num text-[#032352]">{currency(invoice.subtotal)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-[#5B5C5F]">VAT (20%)</span>
            <span className="font-mono-num text-[#032352]">{currency(invoice.vat_total)}</span>
          </div>
          <div
            className="flex justify-between items-center pt-2 border-t-2 mt-2"
            style={{ borderColor: "#032352" }}
          >
            <span className="font-heading font-bold text-[#032352]">Total Due</span>
            <span className="font-mono-num font-bold text-lg text-[#032352]">
              {currency(invoice.total)}
            </span>
          </div>
        </div>
      </div>

      {invoice.notes && (
        <div className="mt-8 pt-4 border-t border-slate-200">
          <p className="text-xs uppercase tracking-wider text-[#5B5C5F] mb-1">Notes</p>
          <p className="text-sm text-[#5B5C5F] whitespace-pre-line">{invoice.notes}</p>
        </div>
      )}

      {(bank.account_number || bank.account_name || bank.iban) && (
          <div className="mt-8 pt-4 border-t border-slate-200" data-testid="invoice-bank-details">
            <p className="text-xs uppercase tracking-wider text-[#5B5C5F] mb-2">Payment Details</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm text-[#032352] max-w-md">
              {bank.account_name && (
                <>
                  <span className="text-[#5B5C5F]">Account Name</span>
                  <span className="font-medium">{bank.account_name}</span>
                </>
              )}
              {bank.bank_name && (
                <>
                  <span className="text-[#5B5C5F]">Bank</span>
                  <span className="font-medium">{bank.bank_name}</span>
                </>
              )}
              {bank.sort_code && (
                <>
                  <span className="text-[#5B5C5F]">Sort Code</span>
                  <span className="font-mono-num font-medium">{bank.sort_code}</span>
                </>
              )}
              {bank.account_number && (
                <>
                  <span className="text-[#5B5C5F]">Account Number</span>
                  <span className="font-mono-num font-medium">{bank.account_number}</span>
                </>
              )}
              {bank.iban && (
                <>
                  <span className="text-[#5B5C5F]">IBAN</span>
                  <span className="font-mono-num font-medium">{bank.iban}</span>
                </>
              )}
            </div>
            {bank.payment_terms && (
              <p className="text-sm text-[#5B5C5F] mt-3 whitespace-pre-line">{bank.payment_terms}</p>
            )}
          </div>
        )}

      <p className="text-center text-xs text-[#5B5C5F] mt-10">
        Thank you for your business · J&amp;L Industrial Supplies
      </p>
    </div>
  );
}
